import { Component, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { NgSelectComponent } from '@ng-select/ng-select';
import { I18NextPipe } from 'angular-i18next';
import { HtmlDecoderService } from 'src/app/services/services/html-decoder.service';

/**
 * BasicDropdownComponent
 *
 * Purpose
 * - PCC-styled dropdown component based on ng-select
 * - Supports single and multiple selection modes
 * - Optional search functionality
 * - Multiple sizes (large, medium, small)
 * - Multiline mode that expands height to accommodate all selected items
 * - Consistent design tokens matching other basic components
 *
 * Visual states
 * - default: standard dropdown with border
 * - hover: subtle border highlight
 * - focus: blue border (#623875) with glow effect
 * - error: red border with error message display
 * - disabled: grayed out appearance, no interaction
 *
 * Features
 * - Optional label above the dropdown
 * - Optional error text with icon below the dropdown
 * - Optional search functionality
 * - Single line or multiline display modes
 * - In multiline mode, height expands to show all selected items
 * - Customizable width (default: 350px)
 * - Two-way data binding for form integration
 *
 * Inputs
 * - items: any[] (required) - Array of items to display in the dropdown
 * - model: any | any[] (default: null) - Selected value(s). Use array for multiple selection
 * - bindLabel: string (optional) - Property name to use as display label for items
 * - bindValue: string (optional) - Property name to use as value for items
 * - placeholder: string (optional) - Placeholder text shown when no selection
 * - multiple: boolean (default: false) - Enable multiple selection
 * - searchable: boolean (default: true) - Enable search functionality
 * - multiline: boolean (default: false) - Display selected items in multiline format (only for multiple selection)
 * - size: 'large' | 'medium' | 'small' (default: 'medium') - Size of the dropdown
 * - width: string (default: '350px') - Custom width for the dropdown (e.g., '100%', '200px')
 * - disabled: boolean (default: false) - Disables interaction and applies disabled styling
 * - label: string (optional) - Label text displayed above the dropdown
 * - hasError: boolean (default: false) - Applies error styling (red border)
 * - errorText: string (optional) - Error message displayed below the dropdown when hasError is true
 * - clearable: boolean (default: true) - Show clear button to remove selection
 * - closeOnSelect: boolean (default: true) - Close dropdown after selection (only for single selection)
 * - renderAsHtml: boolean (default: false) - If true, decodes HTML entities in option labels to display special symbols/emojis (e.g., "&#128222;" becomes 📞)
 * - showSelectAll: boolean (default: false) - If true, in the header there is an option to select/deselect all options
 * - showAllSelectedLabel: boolean (default: false) - If true, instead of showing the selected items it just shows All Items
 *
 * Outputs
 * - modelChange: any | any[] - Emits the new selection on change (supports [(model)] two-way binding)
 * - change: any - Emitted when selection changes (provides the selected item(s))
 *
 * Usage examples
 *
 * <!-- Basic single selection dropdown -->
 * <app-basic-dropdown
 *   [items]="options"
 *   bindLabel="name"
 *   bindValue="id"
 *   [(model)]="selectedId"
 *   placeholder="Select an option">
 * </app-basic-dropdown>
 *
 * <!-- With label and search -->
 * <app-basic-dropdown
 *   [items]="options"
 *   bindLabel="name"
 *   [(model)]="selectedId"
 *   label="Choose Option"
 *   placeholder="Select..."
 *   [searchable]="true">
 * </app-basic-dropdown>
 *
 * <!-- Multiple selection with multiline -->
 * <app-basic-dropdown
 *   [items]="options"
 *   bindLabel="name"
 *   [(model)]="selectedIds"
 *   [multiple]="true"
 *   [multiline]="true"
 *   label="Select Multiple"
 *   placeholder="Choose options...">
 * </app-basic-dropdown>
 *
 * <!-- With error state -->
 * <app-basic-dropdown
 *   [items]="options"
 *   bindLabel="name"
 *   [(model)]="selectedId"
 *   [hasError]="hasValidationError"
 *   errorText="Please select an option">
 * </app-basic-dropdown>
 *
 * <!-- Different sizes -->
 * <app-basic-dropdown [items]="options" size="large" [(model)]="selectedId"></app-basic-dropdown>
 * <app-basic-dropdown [items]="options" size="medium" [(model)]="selectedId"></app-basic-dropdown>
 * <app-basic-dropdown [items]="options" size="small" [(model)]="selectedId"></app-basic-dropdown>
 *
 * <!-- Without search -->
 * <app-basic-dropdown
 *   [items]="options"
 *   [(model)]="selectedId"
 *   [searchable]="false">
 * </app-basic-dropdown>
 */

type DropdownSize = 'large' | 'medium' | 'small';

@Component({
  selector: 'app-basic-dropdown',
  templateUrl: './basic-dropdown.component.html',
  styleUrls: ['./basic-dropdown.component.scss'],
  standalone: false
})
export class BasicDropdownComponent {
  @Input() items: any[] = [];
  @Input() model: any | any[] = null;
  @Output() modelChange = new EventEmitter<any | any[]>();
  @Input() bindLabel: string = '';
  @Input() bindValue: string = '';
  @Input() placeholder: string = 'Select...';
  @Input() multiple: boolean = false;
  @Input() searchable: boolean = true;
  @Input() multiline: boolean = false;
  @Input() size: DropdownSize = 'medium';
  @Input() width: string = '350px';
  @Input() disabled: boolean = false;
  @Input() label: string = '';
  @Input() hasError: boolean = false;
  @Input() errorText: string = '';
  @Input() clearable: boolean = true;
  @Input() closeOnSelect: boolean = true;
  @Input() translateItems: boolean = false;
  @Input() translationNamespace: string = "";
  @Input() appendTo: string = null;
  @Input() renderAsHtml: boolean = false; // If true, decodes HTML entities to display special symbols/emojis
  @Input() showRadioButton: boolean = false;
  @Input() showIcon: boolean = true;
  @Input() showSelectAll: boolean = false;
  @Input() selectAllText: string = 'Select All';
  @Input() unselectAllText: string = 'Unselect All';
  @Input() showAllSelectedLabel: boolean = false;
  @Input() allSelectedLabel: string = 'All Selected';

  @Output() change = new EventEmitter<any>();

  @ViewChild(NgSelectComponent, { static: false }) ngSelect!: NgSelectComponent;

  constructor(private i18nextPipe: I18NextPipe, private htmlDecoderService: HtmlDecoderService) { }

  searchTerm: string = '';

  get shouldCloseOnSelect(): boolean {
    // In multiselect mode, prevent closing after click
    if (this.multiple) {
      return false;
    }
    return this.closeOnSelect;
  }

  get shouldShowSearchable(): boolean {
    // When searchable is true, we use custom search input, so disable built-in search
    return false;
  }

  onModelChange(value: any | any[]): void {
    this.model = value;
    this.modelChange.emit(value);
    this.change.emit(value);
  }

  onSearchInput(event: any): void {
    this.searchTerm = event;
    if (this.ngSelect) {
      this.ngSelect.filter(event);
    }
  }

  itemText(id: number) {
    let item = this.items?.find((i) => i[this.bindValue] == id)
    return item ? item[this.bindLabel] : '';
  }

  itemExists(id: number) {
    return this.items?.find((i) => i[this.bindValue] == id) != undefined;
  }

  clearItem(ngSelectControl: NgSelectComponent, id: number) {
    ngSelectControl.clearItem(this.items?.find((i) => i[this.bindValue] == id));
  }

  customSearchFn = (term: string, item: any) => {
    let name = item[this.bindLabel];
    if (this.translateItems) {
      name = this.i18nextPipe.transform(this.translationNamespace ? this.translationNamespace + ':' + name : name)
    }
    return name.toLocaleLowerCase().indexOf(term.toLocaleLowerCase()) > -1;
  }

  get selectedItemsCount(): number {
    if (!this.model) return 0;
    if (Array.isArray(this.model)) {
      return this.model.length;
    }
    return 1;
  }

  /**
   * Gets the display text for an item, optionally decoding HTML entities
   * @param item The item object
   * @param translate Whether to translate the text
   * @returns The display text, with HTML entities decoded if renderAsHtml is true
   */
  getDisplayText(item: any, translate: boolean = false): string {
    if (!item) return '';
    let text = '';
    if (translate && this.translationNamespace) {
      text = this.i18nextPipe.transform(this.translationNamespace + ':' + item[this.bindLabel]);
    } else {
      text = item[this.bindLabel] || '';
    }
    // Only decode HTML entities if renderAsHtml is enabled
    if (this.renderAsHtml) {
      return this.htmlDecoderService.decodeHtmlEntities(text);
    }
    return text;
  }

  get isAllSelected(): boolean {
    if (!this.multiple) return false;
    if (!Array.isArray(this.model)) return false;
    if (!this.items || this.items.length === 0) return false;

    // If nothing selected → definitely NOT all selected
    if (this.model.length === 0) return false;

    // If using bindValue, compare by value
    if (this.bindValue) {
      const allValues = this.items.map(i => i[this.bindValue]);
      const selectedSet = new Set(this.model);
      return allValues.every(v => selectedSet.has(v));
    }

    // Otherwise compare by object reference
    const selectedSet = new Set(this.model);
    return this.items.every(i => selectedSet.has(i));
  }

  toggleSelectAll(): void {
    if (this.disabled) return;

    if (this.isAllSelected) {
      this.onModelChange([]);
      return;
    }

    // Select all
    if (this.bindValue) {
      const allValues = this.items.map(i => i[this.bindValue]);
      this.onModelChange(allValues);
    } else {
      this.onModelChange([...this.items]);
    }
  }

  get shouldShowAllSelectedLabel(): boolean {
    return this.showAllSelectedLabel && this.isAllSelected;
  }
}